import { useState } from 'react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import {
  Button,
  CheckboxField,
  Modal,
  ModalControlModalProps,
  Text,
  useControlledField,
} from '@frontend/design-system';
import { useDeleteConfirmationShallowStore } from '../../stores/delete-confirmation-store';

type DeleteFaxConfirmationModalProps = {
  modalProps: ModalControlModalProps;
  onDelete: () => void;
  deleteType: 'fax' | 'draft';
};

export const DeleteFaxConfirmationModal = ({ modalProps, onDelete, deleteType }: DeleteFaxConfirmationModalProps) => {
  const { t } = useTranslation('fax');
  const { setSkipFaxDeleteConfirmation, setSkipDraftDeleteConfirmation } = useDeleteConfirmationShallowStore(
    'setSkipFaxDeleteConfirmation',
    'setSkipDraftDeleteConfirmation'
  );
  const [dontAskAgain, setDontAskAgain] = useState(false);

  const dontAskAgainProps = useControlledField({
    type: 'checkbox',
    value: dontAskAgain,
    onChange: setDontAskAgain,
  });

  const handleDelete = () => {
    if (dontAskAgain) {
      if (deleteType === 'fax') {
        setSkipFaxDeleteConfirmation(true);
      } else {
        setSkipDraftDeleteConfirmation(true);
      }
    }
    onDelete();
    setDontAskAgain(false);
    modalProps.onClose();
  };

  const handleCancel = () => {
    setDontAskAgain(false);
    modalProps.onClose();
  };

  const title = deleteType === 'fax' ? t('Delete Fax') : t('Delete Draft');
  const message =
    deleteType === 'fax'
      ? t('This action is irreversible. Are you sure you want to delete this fax?')
      : t('This action is irreversible. Are you sure you want to delete this draft?');

  return (
    <Modal {...modalProps}>
      <Modal.Header onClose={handleCancel}>{title}</Modal.Header>
      <Modal.Body>
        <Text>{message}</Text>
      </Modal.Body>
      <Modal.Footer>
        <div
          css={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <CheckboxField {...dontAskAgainProps} label={t("Don't ask me again")} name='dont-ask-again' />
          <div css={{ display: 'flex', gap: theme.spacing(2) }}>
            <Button variant='secondary' size='large' onClick={handleCancel}>
              {t('Cancel')}
            </Button>
            <Button variant='primary' size='large' destructive onClick={handleDelete}>
              {t('Delete')}
            </Button>
          </div>
        </div>
      </Modal.Footer>
    </Modal>
  );
};
